/**
 * Merge streamed text updates robustly.
 *
 * Supports both server styles:
 * - **delta**: incoming is the next chunk to append
 * - **accumulated**: incoming is the full content-so-far
 *
 * Also defends against duplicate deliveries and overlapping chunks.
 */
export const mergeStreamingText = (previousText: string, incomingText: string): string => {
  const previous = typeof previousText === 'string' ? previousText : '';
  const incoming = typeof incomingText === 'string' ? incomingText : '';

  if (!incoming) return previous;
  if (!previous) return incoming;

  // Exact match or obvious duplication.
  if (incoming === previous) return previous;
  if (previous.endsWith(incoming)) return previous;

  // Accumulated update (incoming contains previous as a prefix).
  if (incoming.startsWith(previous)) return incoming;

  // Stale/partial update.
  if (previous.startsWith(incoming)) return previous;

  // Overlap case: previous suffix overlaps incoming prefix.
  const maxOverlap = Math.min(previous.length, incoming.length);
  for (let overlapLength = maxOverlap; overlapLength > 0; overlapLength -= 1) {
    if (previous.endsWith(incoming.slice(0, overlapLength))) {
      return previous + incoming.slice(overlapLength);
    }
  }

  // Fallback: treat as a plain delta.
  return previous + incoming;
};

